import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Upload, Link2, ExternalLink, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";

export default function BannerManager({ placement = "home", title = "Banners de publicidade", hint = "Aparecem no topo do site, abaixo da logo." }) {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [link, setLink] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/banners", { params: { placement } });
      setBanners(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [placement]);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/admin/banners?link=${encodeURIComponent(link.trim())}&placement=${placement}`, fd);
      toast.success("Banner enviado!");
      setLink("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success("Banner removido.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const move = async (index, dir) => {
    const next = [...banners];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBanners(next);
    try {
      await api.post("/admin/banners/reorder", { ids: next.map((b) => b.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  return (
    <div className="mt-8" data-testid={placement === "home" ? "banner-manager" : `banner-manager-${placement}`}>
      <div className="flex items-center gap-2">
        <ImagePlus className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
          {title}
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        {hint} Use as setas para ordenar. Recomendado: 1600×500px.
      </p>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={`banner-link-input-${placement}`}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Link ao clicar (opcional): https://..."
              className="rounded-xl border-slate-200 pl-9"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={onFile}
            className="hidden"
            data-testid={`banner-file-input-${placement}`}
          />
          <Button
            data-testid={`banner-upload-btn-${placement}`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-xl bg-[#1B7A45] px-6 py-5 font-semibold text-white hover:bg-[#156337] disabled:opacity-50"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? "Enviando..." : "Enviar banner"}
          </Button>
        </div>

        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
          ) : banners.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">
              Nenhum banner ainda. Envie o primeiro acima.
            </p>
          ) : (
            banners.map((b, i) => (
              <div
                key={b.id}
                data-testid={`banner-item-${b.id}`}
                className="flex items-center gap-3 rounded-xl border border-slate-200 p-2"
              >
                <div className="flex flex-col">
                  <button
                    data-testid={`banner-up-${b.id}`}
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`banner-down-${b.id}`}
                    onClick={() => move(i, 1)}
                    disabled={i === banners.length - 1}
                    className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </div>
                <img
                  src={`${API}/banners/${b.id}/image`}
                  alt="banner"
                  className="h-16 w-28 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">
                    {b.original_filename || "banner"}
                  </p>
                  {b.link ? (
                    <a
                      href={b.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 truncate text-xs text-[#1B7A45] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" /> {b.link}
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">Sem link</span>
                  )}
                </div>
                <Button
                  data-testid={`banner-delete-${b.id}`}
                  onClick={() => remove(b.id)}
                  size="sm"
                  variant="outline"
                  className="rounded-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
